import { FC, ReactNode, useState } from 'react';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';

import { CustomInput, CustomSelect, Text } from '../../shared/uiComponents';
import { TEXT as TEXT_COLORS } from '../../shared/Helpers/colors';
import { CancellingParty } from '../../redux/API/ClientAPIHelpers/scheduleCancellation';

// --- Hook ---

interface OtherReasonCheck {
	(reasonId: string): boolean;
}

export const useCancellationForm = (isOtherReasonCheck: OtherReasonCheck) => {
	const [cancellingParty, setCancellingParty] = useState<CancellingParty | null>(null);
	const [reasonId, setReasonId] = useState('');
	const [notes, setNotes] = useState('');
	const [submitted, setSubmitted] = useState(false);

	const handleCancellingPartyChange = (party: CancellingParty) => {
		setCancellingParty(party);
		setReasonId('');
	};

	const isAgency = cancellingParty === CancellingParty.Agency;
	const isOtherReason = reasonId !== '' && isOtherReasonCheck(reasonId);
	const notesRequired = isAgency || isOtherReason;

	const isFormValid =
		cancellingParty !== null &&
		(isAgency || reasonId !== '') &&
		(!notesRequired || notes.trim() !== '');

	return {
		cancellingParty,
		handleCancellingPartyChange,
		isAgency,
		isFormValid,
		isOtherReason,
		notes,
		notesRequired,
		reasonId,
		setNotes,
		setReasonId,
		setSubmitted,
		submitted,
	};
};

// --- Shared Form Fields Component ---

export interface CancellingPartyOption {
	id: string;
	label: string;
	description: string;
}

interface CancellationFormFieldsProps {
	beforeNotes?: ReactNode;
	cancellingParty: CancellingParty | null;
	cancellingPartyOptions: CancellingPartyOption[];
	className?: string;
	inputClassName?: string;
	isAgency: boolean;
	isOtherReason: boolean;
	notes: string;
	notesPlaceholder?: string;
	notesRequired: boolean;
	onCancellingPartyChange: (party: CancellingParty) => void;
	onNotesChange: (value: string) => void;
	onReasonChange: (value: string) => void;
	partyLabel?: string;
	reasonId: string;
	reasonOptions: { id: string; label: string }[];
	selectClassName?: string;
	submitted: boolean;
}

const CancellationFormFields: FC<CancellationFormFieldsProps> = ({
	beforeNotes,
	cancellingParty,
	cancellingPartyOptions,
	className = 'cancellationField',
	inputClassName,
	isAgency,
	isOtherReason,
	notes,
	notesPlaceholder,
	notesRequired,
	onCancellingPartyChange,
	onNotesChange,
	onReasonChange,
	partyLabel = 'Who is canceling?',
	reasonId,
	reasonOptions,
	selectClassName,
	submitted,
}) => {
	const defaultPlaceholder = isOtherReason
		? 'Please provide an explanation for this cancellation...'
		: 'Any additional context or information...';

	return (
		<>
			<div className={className}>
				<div className="fieldLabelRow">
					<Text title={partyLabel} type="psm" fontWeight="semiBold" />
					<span className="requiredAsterisk">*</span>
				</div>
				<RadioGroup
					value={cancellingParty === null ? '' : `${cancellingParty}`}
					onChange={(_e, val) => onCancellingPartyChange(Number(val))}
				>
					{cancellingPartyOptions.map((option) => (
						<div
							key={option.id}
							className={`cancellingPartyOption ${cancellingParty === Number(option.id) ? 'selected' : ''} ${submitted && cancellingParty === null ? 'fieldError' : ''}`}
							onClick={() => onCancellingPartyChange(Number(option.id))}
						>
							<FormControlLabel value={option.id} control={<Radio />} label="" sx={{ mr: 0 }} />
							<div>
								<Text title={option.label} type="psm" fontWeight="medium" />
								<Text title={option.description} type="pxs" color={TEXT_COLORS.TERTIARY} />
							</div>
						</div>
					))}
				</RadioGroup>
			</div>

			{!isAgency && (
				<div className={className}>
					<div className="fieldLabelRow">
						<Text title="Reason for cancellation" type="psm" fontWeight="semiBold" />
						<span className="requiredAsterisk">*</span>
					</div>
					<CustomSelect
						className={selectClassName}
						placeholder="Select a reason..."
						data={reasonOptions}
						value={reasonId}
						setValue={(e) => onReasonChange(e.target.value)}
						error={submitted && reasonId === ''}
					/>
				</div>
			)}

			{beforeNotes}

			<div className={className}>
				<div className="fieldLabelRow">
					<Text
						title={isAgency ? 'Notes' : isOtherReason ? 'Please explain' : 'Additional notes (optional)'}
						type="psm"
						fontWeight="semiBold"
					/>
					{notesRequired && <span className="requiredAsterisk">*</span>}
				</div>
				<CustomInput
					className={inputClassName}
					value={notes}
					onChange={(e) => onNotesChange(e.target.value)}
					multiline
					error={submitted && notesRequired && notes.trim() === ''}
					errorMessage={
						submitted && notesRequired && notes.trim() === ''
							? 'Please provide an explanation for the cancellation.'
							: ''
					}
					placeholder={notesPlaceholder ?? defaultPlaceholder}
				/>
			</div>
		</>
	);
};

export default CancellationFormFields;
